import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { formatCurrency } from '@/lib/currency';
import { format } from 'date-fns';
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Target, 
  BarChart3, 
  Calendar,
  ExternalLink,
  Filter
} from 'lucide-react';

interface UtmAnalyticsProps {
  companyId: string;
}

interface UtmData {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  total_bookings: number;
  total_revenue: number;
  avg_booking_value: number;
  conversion_rate: number;
  first_touch_bookings: number;
  last_touch_bookings: number;
}

interface AttributionData {
  attribution_type: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  total_bookings: number;
  total_revenue: number;
  percentage_of_total: number;
}

interface CampaignData {
  utm_campaign: string;
  utm_source: string;
  utm_medium: string;
  total_bookings: number;
  total_revenue: number;
  avg_booking_value: number;
  best_performing_source: string;
  best_performing_medium: string;
  campaign_roi: number;
}

export default function UtmAnalytics({ companyId }: UtmAnalyticsProps) {
  const [utmData, setUtmData] = useState<UtmData[]>([]);
  const [attributionData, setAttributionData] = useState<AttributionData[]>([]);
  const [campaignData, setCampaignData] = useState<CampaignData[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [selectedTab, setSelectedTab] = useState('overview');

  useEffect(() => {
    loadUtmAnalytics();
  }, [companyId, dateRange]);

  const loadUtmAnalytics = async () => {
    try {
      setLoading(true);
      const dateFrom = new Date();
      dateFrom.setDate(dateFrom.getDate() - parseInt(dateRange));
      
      // Load UTM analytics
      const { data: utmAnalytics, error: utmError } = await supabase.rpc(
        'get_utm_analytics',
        {
          p_company_id: companyId,
          p_date_from: dateFrom.toISOString().split('T')[0],
          p_date_to: new Date().toISOString().split('T')[0]
        }
      );

      if (utmError) throw utmError;
      setUtmData(utmAnalytics || []);

      // Load attribution data
      const { data: attribution, error: attributionError } = await supabase.rpc(
        'get_utm_attribution',
        {
          p_company_id: companyId,
          p_date_from: dateFrom.toISOString().split('T')[0],
          p_date_to: new Date().toISOString().split('T')[0]
        }
      );

      if (attributionError) throw attributionError;
      setAttributionData(attribution || []);

      // Load campaign performance
      const { data: campaigns, error: campaignError } = await supabase.rpc(
        'get_utm_campaign_performance',
        {
          p_company_id: companyId,
          p_date_from: dateFrom.toISOString().split('T')[0],
          p_date_to: new Date().toISOString().split('T')[0]
        }
      );

      if (campaignError) throw campaignError;
      setCampaignData(campaigns || []);

    } catch (error) {
      console.error('Error loading UTM analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTotalStats = () => {
    const totalBookings = utmData.reduce((sum, item) => sum + Number(item.total_bookings), 0);
    const totalRevenue = utmData.reduce((sum, item) => sum + Number(item.total_revenue), 0);
    const avgConversion = utmData.length > 0 
      ? utmData.reduce((sum, item) => sum + Number(item.conversion_rate), 0) / utmData.length 
      : 0;
    
    return { totalBookings, totalRevenue, avgConversion };
  };

  const stats = getTotalStats();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="app-panel animate-pulse border border-slate-200/70">
              <CardContent className="p-6">
                <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-slate-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with date range selector */}
      <div className="app-panel flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-950">UTM Analytics</h2>
          <p className="text-slate-600">
            Track your marketing campaigns and attribution
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadUtmAnalytics}>
            <Filter className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="app-panel border border-slate-200/70">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Bookings</p>
                <p className="text-2xl font-bold text-slate-950">{stats.totalBookings}</p>
              </div>
              <Users className="h-8 w-8 text-sky-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="app-panel border border-slate-200/70">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Revenue</p>
                <p className="text-2xl font-bold text-slate-950">{formatCurrency(stats.totalRevenue)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-emerald-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="app-panel border border-slate-200/70">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Avg Conversion</p>
                <p className="text-2xl font-bold text-slate-950">{stats.avgConversion.toFixed(1)}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-violet-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analytics */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="border border-slate-200 bg-white/80">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="attribution">Attribution</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card className="app-panel border border-slate-200/70">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-slate-950">
                <BarChart3 className="h-5 w-5 text-sky-300" />
                UTM Source Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {utmData.length === 0 ? (
                <div className="text-center py-10 text-slate-600">
                  <Target className="h-12 w-12 mx-auto mb-4 opacity-70 text-slate-400" />
                  <p>No UTM data available for the selected period</p>
                  <p className="mt-1 text-sm text-slate-500">Start tracking your marketing campaigns to unlock channel-level visibility here.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {utmData.map((item, index) => (
                    <div key={index} className="rounded-[1rem] border border-slate-200/70 bg-white/80 p-4 shadow-soft">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{item.utm_source || 'Direct'}</Badge>
                          <Badge variant="secondary">{item.utm_medium || 'Unknown'}</Badge>
                          {item.utm_campaign && (
                            <Badge variant="default">{item.utm_campaign}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-slate-700">
                        <div className="text-center">
                          <p className="font-medium">{item.total_bookings}</p>
                          <p className="text-slate-500">Bookings</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">{formatCurrency(Number(item.total_revenue))}</p>
                          <p className="text-slate-500">Revenue</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">{item.conversion_rate.toFixed(1)}%</p>
                          <p className="text-slate-500">Conversion</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">{formatCurrency(Number(item.avg_booking_value))}</p>
                          <p className="text-slate-500">Avg Value</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attribution" className="space-y-4">
          <Card className="app-panel border border-slate-200/70">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-slate-950">
                <Target className="h-5 w-5 text-violet-300" />
                Attribution Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              {attributionData.length === 0 ? (
                <div className="text-center py-10 text-slate-600">
                  <Target className="h-12 w-12 mx-auto mb-4 opacity-70 text-slate-400" />
                  <p>No attribution data available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {attributionData.map((item, index) => (
                    <div key={index} className="rounded-[1rem] border border-slate-200/70 bg-white/80 p-4 shadow-soft">
                      <div className="flex items-center gap-4">
                        <Badge variant={item.attribution_type === 'first_touch' ? 'default' : 'secondary'}>
                          {item.attribution_type === 'first_touch' ? 'First Touch' : 'Last Touch'}
                        </Badge>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{item.utm_source || 'Direct'}</Badge>
                          <Badge variant="secondary">{item.utm_medium || 'Unknown'}</Badge>
                          {item.utm_campaign && (
                            <Badge variant="default">{item.utm_campaign}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-slate-700">
                        <div className="text-center">
                          <p className="font-medium">{item.total_bookings}</p>
                          <p className="text-slate-500">Bookings</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">{formatCurrency(Number(item.total_revenue))}</p>
                          <p className="text-slate-500">Revenue</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">{item.percentage_of_total.toFixed(1)}%</p>
                          <p className="text-slate-500">of Total</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="campaigns" className="space-y-4">
          <Card className="app-panel border border-slate-200/70">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-slate-950">
                <Calendar className="h-5 w-5 text-amber-300" />
                Campaign Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {campaignData.length === 0 ? (
                <div className="text-center py-10 text-slate-600">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-70 text-slate-400" />
                  <p>No campaign data available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {campaignData.map((item, index) => (
                    <div key={index} className="rounded-[1rem] border border-slate-200/70 bg-white/80 p-4 shadow-soft">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="default">{item.utm_campaign}</Badge>
                          <Badge variant="outline">{item.utm_source}</Badge>
                          <Badge variant="secondary">{item.utm_medium}</Badge>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-slate-950">{formatCurrency(Number(item.campaign_roi))}</p>
                          <p className="text-xs text-slate-500">ROI per booking</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-slate-700">
                        <div className="text-center">
                          <p className="font-medium">{item.total_bookings}</p>
                          <p className="text-slate-500">Bookings</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">{formatCurrency(Number(item.total_revenue))}</p>
                          <p className="text-slate-500">Revenue</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">{formatCurrency(Number(item.avg_booking_value))}</p>
                          <p className="text-slate-500">Avg Value</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium text-xs">{item.best_performing_source}</p>
                          <p className="text-slate-500">Best Source</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
